import PropTypes from 'prop-types';

import Container, { ContainerProps as ContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { ArrowBackIos } from '@mui/icons-material';
import { fontWeight } from '@mui/system';

export interface PitchProps extends ContainerProps {
  title: string;
  isSmallerHeader: string;
  description: string;
  titleImageUrl?: string;
  items: {
    title: string;
    description: string;
    imageUrl: string;
  }[];
  background?: string;
  border?: string;
  hideArrow?: boolean;
  showTopImage?: boolean;
}

export const Pitch = ({
  className,
  title,
  description,
  titleImageUrl,
  items,
  background,
  border,
  hideArrow,
  maxWidth,
  showTopImage,
  ...props
}: PitchProps) => {
  const screens = useAppMediaQuery();
  const smallScreens = screens.isMobile || screens.isTablet;

  const scrollToSection = () => {
    document.getElementById('main-content').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ background, border }}>
      <Container
        className={className}
        sx={{
          pt: 9,
        }}
        {...props}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
            }}
          >
            <Stack
              sx={{
                maxWidth: '500px',
                my: 2,
                mx: 'auto',
                textAlign: { xs: 'left', md: 'center' },
                gap: 0.5,
              }}
            >
              <Typography
                variant={props.isSmallerHeader ? 'subtitle1' : 'h4'}
                variantMapping={{ subtitle1: 'h2', h4: 'h2' }}
              >
                {title}
              </Typography>
              <Typography variant='body1'>{description}</Typography>
            </Stack>

            {showTopImage && <img src={titleImageUrl} alt={''} width={275} height={275} />}
          </Box>

          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}
          >
            {items?.map((item, index) => (
              <Stack key={index} sx={{ gap: 3.5, py: 2 }}>
                <img src={item?.imageUrl} alt={item?.title} width={48} height={48} />
                <Typography
                  variant='subtitle3'
                  variantMapping={{ subtitle3: 'h2' }}
                  sx={{ fontWeight: 'normal' }}
                >
                  {item?.title}
                </Typography>
                <Typography variant='body2'>{item?.description}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>

        {!hideArrow && (
          <Box sx={{ width: 'fit-content', mx: 'auto', pb: 8, pt: 4 }}>
            <ArrowBackIos
              onClick={scrollToSection}
              sx={{
                width: '24px',
                height: '24px',
                transform: 'rotate(-90deg)',
                color: globalColours['$global-colour-interactive-primary-default'],
                cursor: 'pointer',
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

Pitch.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  isSmallerHeader: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  titleImageUrl: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired,
    }),
  ).isRequired,
  background: PropTypes.string,
  border: PropTypes.string,
  hideArrow: PropTypes.bool,
  showTopImage: PropTypes.bool,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Pitch.defaultProps = {
  children: null,
  className: null,
  isSmallerHeader: false,
  background: 'transparent',
  border: 'none',
  hideArrow: false,
  showTopImage: false,
  maxWidth: 'lg',
};

export default styled<any>(Pitch)(
  ({ theme }) => `
`,
);
